import { IconLayoutGrid2 } from "central-icons/IconLayoutGrid2";
import { useCallback, type FC } from "react";
import type { NavigateFunction } from "react-router-dom";

import { commandItemIconClass } from "@/components/command-palette/constants";
import { CommandEmpty, CommandGroup, CommandItem, CommandLoading } from "@/components/flexkit/Command";
import { useProjectListPage, type PlainProject } from "@/queries/project-queries";

type ProjectsPageProps = {
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
};

export const ProjectsPage: FC<ProjectsPageProps> = ({ runCommand, navigate }) => {
    const { data, isPending } = useProjectListPage({ enabled: true });
    const projects = data?.projects ?? [];

    if (isPending) {
        return <CommandLoading>Loading projects...</CommandLoading>;
    }

    if (projects.length === 0) {
        return <CommandEmpty>No projects found.</CommandEmpty>;
    }

    return (
        <CommandGroup heading="Projects">
            {projects.map((project) => (
                <ProjectItem key={project.id} project={project} runCommand={runCommand} navigate={navigate} />
            ))}
        </CommandGroup>
    );
};

const ProjectItem: FC<{
    project: PlainProject;
    runCommand: (fn: () => void) => void;
    navigate: NavigateFunction;
}> = ({ project, runCommand, navigate }) => {
    const name = project.metadata?.name || "Untitled project";
    const handleSelect = useCallback(
        () => runCommand(() => navigate(`/projects/${project.id}`)),
        [runCommand, navigate, project.id],
    );

    return (
        <CommandItem value={`${name} project`} onSelect={handleSelect}>
            <IconLayoutGrid2 className={commandItemIconClass} />
            <span className="truncate">{name}</span>
        </CommandItem>
    );
};
